// UPDATE AS OF 17/10/2022, Anonymous data capture is no longer anonymous as member ID is also sent

import { Device } from '@capacitor/device';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { callEndpoint, telemetry } from '../../backend';
import { hasFeatureEnabled } from '../../feature';
import {
  get as getFromLocalStorage,
  set as putIntoLocalStorage,
} from '../../local-storage';
import captureSpecificError from '../../sentry';
import {
  clearEventsIfNotSession,
  createTable,
  deleteRecordsBeforeTimestamp,
  getFirstNRecords,
  insertRecord,
  isSQLLiteAvailable,
} from '../../sql-lite';
import { getMemberSignalSharingSettings } from '../../store/modules/app-controls/actions';

const UPLOAD_THRESHOLD = 5;
const UPLOAD_LIMIT = 100;
const sessionId = uuidv4();

let cachedDeviceId;

export const getDeviceId = async () => {
  if (cachedDeviceId) {
    return cachedDeviceId;
  }
  try {
    const dId = await Device.getId();
    cachedDeviceId = dId.uuid || dId.identifier;
    return cachedDeviceId;
  } catch (e) {
    // This code exists to handle the case where the device plugin is not available or throws an error trying to determine a device id.
    // If there was a bug with capacitors getId and then it gets fixed, there is the potential that deviceId's might change. However,
    // I don't expect that to ever happen as Device.getId will create a device is regardless of platform.
    let deviceIdFromStorage = getFromLocalStorage('eventCapture', 'deviceId');
    if (!deviceIdFromStorage) {
      deviceIdFromStorage = uuidv4();
      await putIntoLocalStorage(
        'eventCapture',
        'deviceId',
        deviceIdFromStorage,
      );
    }

    return deviceIdFromStorage;
  }
};

const uploadEvents = async (events) => {
  const deviceId = await getDeviceId();
  if (deviceId) {
    await callEndpoint(callEndpoint.endpoints.uploadDataCaptureEvents, {
      deviceId,
      events,
    });
  } else {
    captureSpecificError(
      new Error('No device id'),
      'containers/app-main-client/src/services/event-capture/anonymous-data-capture/index.js',
      {
        deckeeId: '[captureEvent] uploadEvents no device id',
      },
      { deviceId: deviceId || '' },
    );
    telemetry(
      telemetry.NOTIFY_FREEBOARD_CHANNEL,
      `[eventCapture.captureEvent] failed to upload events because no deviceId: ${JSON.stringify(
        events,
      )}`,
    );
  }
};

const storeEvent = async ({
  type,
  latitude,
  longitude,
  timestamp,
  otherProperties,
}) => {
  await insertRecord(
    'events',
    ['type', 'payload', 'sessionId', 'latitude', 'longitude', 'timestamp'],
    [
      type,
      JSON.stringify(otherProperties),
      sessionId,
      latitude,
      longitude,
      timestamp,
    ],
  );
};

const getFirstNEvents = async (limit) => getFirstNRecords('events', limit);

const deleteEventsBeforeTimestamp = async (timestamp) =>
  deleteRecordsBeforeTimestamp('events', timestamp);

let currentlyUploadingAndDeleting = false;

/* */
// eslint-disable-next-line @typescript-eslint/no-empty-function
export const uploadAndDelete = async (force = false, cb = () => {}) => {
  if (currentlyUploadingAndDeleting) {
    return;
  }

  currentlyUploadingAndDeleting = true;

  /*
  Multiple things here:
  1. There was / is an issue with existing events stored on a users device that have not been cleared out that don't have a sessionId.
  2. Introduce an upper limit. This is to reduce the sizes of the payloads sent to the third party api.
  */
  const events = (await getFirstNEvents(UPLOAD_LIMIT)).filter(
    (event) => event?.sessionId && event?.timestamp,
  );

  if (events.length > 0 && (events.length > UPLOAD_THRESHOLD || force)) {
    const manipulatedEvents = events.map((event) => {
      const { payload, ...eventData } = event;

      return {
        ...eventData,
        payload: JSON.stringify(JSON.parse(payload)),
      };
    });
    const deviceId = await getDeviceId();
    // Using finally's in this block since it's not necessary to keep the events if they have failed or succeeded.
    uploadEvents(manipulatedEvents)
      .catch((error) => {
        telemetry(
          telemetry.WARN,
          `[captureEvent/uploadAndDelete] uploading events failed: ${error}`,
        );
        captureSpecificError(
          error,
          'containers/app-main-client/src/services/event-capture/anonymous-data-capture/index.js',
          {
            deckeeId: '[uploadAndDelete] upload and delete events failed',
          },
          { events: manipulatedEvents, deviceId },
        );
      })
      .finally(() => {
        deleteEventsBeforeTimestamp(
          manipulatedEvents[manipulatedEvents?.length - 1]?.timestamp,
        )
          .catch((error) => {
            telemetry(
              telemetry.WARN,
              `[captureEvent/uploadAndDelete] deleting events failed: ${error}`,
            );
            captureSpecificError(
              error,
              'containers/app-main-client/src/services/event-capture/anonymous-data-capture/index.js',
              {
                deckeeId:
                  '[uploadAndDelete] deleteEventsBeforeTimestamp failed',
              },
              {
                timestampToDeleteBefore:
                  manipulatedEvents[manipulatedEvents?.length - 1]?.timestamp,
              },
            );
          })
          .finally(() => {
            currentlyUploadingAndDeleting = false;
          });
        cb();
      });
  } else {
    currentlyUploadingAndDeleting = false;
  }
};

/**
 * Throw consistently formatted TypeError for type checking
 * @param {string} fullPropertyName - Property name (ie, key.subkey.subsubkey)
 * @param {any} value - Value within property
 * @param {string} expectedType - Expected type of value
 */
const throwPropertyError = ({ fullPropertyName, value, expectedType }) => {
  captureSpecificError(
    `Key: ${fullPropertyName} in anonymous-data-capture payload with value: ${value} does not match expected key type: ${expectedType} as captured in previous payloads`,
    'containers/app-main-client/src/services/event-capture/anonymous-data-capture/index.js',
  );
  throw new TypeError(
    `Key: ${fullPropertyName} in anonymous-data-capture payload with value: ${value} does not match expected key type: ${expectedType} as captured in previous payloads`,
  );
};

// We store this in memory in the case of if a property changes type later on
const payloadSchema = {};

/**
 * Type check object (and all nested objects) against previously checked objects
 * Throws error when failed check
 * @param {object} object - object to be checked
 * @param {string} sourceKey - key previously checked to build full name of key (object.key.subkey)
 * @return {boolean} True when passed otherwise throws error
 */
const recursiveObjectTypeCheck = (object, sourceKey = '') => {
  Object.entries(object).forEach(([property, value]) => {
    const fullPropertyName = `${sourceKey}.${property}`;
    const propertyToCheck = value;
    if (propertyToCheck !== null) {
      const propertyToCheckType = typeof propertyToCheck;
      // If in payload schema, check type for existing property
      if (fullPropertyName in payloadSchema) {
        const expectedType = payloadSchema[`${fullPropertyName}`];

        if (Array.isArray(object[property])) {
          if (expectedType !== 'array') {
            throwPropertyError({
              fullPropertyName,
              value,
              expectedType,
            });
          }
        } else if (propertyToCheckType !== expectedType) {
          throwPropertyError({
            fullPropertyName,
            value,
            expectedType,
          });
        }
      }
      // if not in payload schema and is array add to payload schema
      else if (Array.isArray(object[property])) {
        payloadSchema[fullPropertyName] = 'array';
      }
      // If not in payload schema, add to payload schema
      else {
        payloadSchema[fullPropertyName] = propertyToCheckType;
      }
      // Recurse into any nested object properties
      if (propertyToCheckType === 'object') {
        recursiveObjectTypeCheck(object[property], fullPropertyName);
      }
    }
  });
  return true;
};

/**
 * Capture an event and try to store it in a SQL database
 * Upload to app database when enough events have been recorded
 * If SQL database isn't available, then upload to database immediately.
 *
 * `inputObject.type`: type of the event, e.g. scheduled, triggered, onChange.
 *
 * `inputObject.payload`: information about the event.
 *
 * @param {{type: string, payload: object}} inputObject
 */
export const captureEvent = async ({ type, payload }, force = false) => {
  const { latitude, longitude, timestamp, ...otherProperties } = payload;
  const signalSettings = getMemberSignalSharingSettings();
  const extendedProperties = { ...otherProperties, ...signalSettings };
  if ((await isSQLLiteAvailable()) && hasFeatureEnabled('enable-sqlite')) {
    await storeEvent({
      type,
      latitude,
      longitude,
      timestamp,
      otherProperties: extendedProperties,
    });

    uploadAndDelete(force);
  } else {
    // JSON stringify the payload (endpoint expects a stringified payload)
    const stringifiedPayload = JSON.stringify(extendedProperties);
    const manipulatedEvent = {
      type,
      latitude,
      longitude,
      timestamp,
      payload: stringifiedPayload,
      sessionId,
    };
    // This check was introduced as mismatched records breaks Athena's parsing
    // We do a check here to make sure we aren't putting in payloads that can't be parsed by Athena
    // Ie, mismatching types for similar keys in objects
    // If there's two objects that both have the key longitude but one's a string and one's a float
    // We want to raise this as an error here
    recursiveObjectTypeCheck(manipulatedEvent);

    // Put the event into an array (endpoint expects an array of events)
    uploadEvents([manipulatedEvent]).catch((e) => {
      captureSpecificError(
        e,
        'containers/app-main-client/src/services/event-capture/anonymous-data-capture/index.js',
        {
          deckeeId: '[captureEvent] uploadEvents failed',
        },
        { manipulatedEvent },
      );
    });
  }
};

export const identify = async () => {
  await captureEvent({
    type: 'identify',
    payload: {
      latitude: null,
      longitude: null,
      timestamp: DateTime.now().toUTC().toISO(),
    },
  });
};

export const getSessionId = () => sessionId;

export const initialize = async () => {
  try {
    if (await isSQLLiteAvailable()) {
      await createTable('events', {
        type: 'string',
        payload: 'text', // Going to store the payload as a json blob
        sessionId: 'string',
        latitude: 'float',
        longitude: 'float',
      });
    }
  } catch (e) {
    captureSpecificError(
      e,
      'containers/app-main-client/src/services/event-capture/anonymous-data-capture/index.js',
      {
        deckeeId: '[initialize] anonymous-data-capture create table',
      },
    );
  }

  // Clear out events that aren't related to the current session id
  // - this is a bug where users can collect events for sessions they are no longer on and
  //   cause event capture and signal to stop working
  try {
    await clearEventsIfNotSession(sessionId);
  } catch (e) {
    captureSpecificError(
      e,
      'containers/app-main-client/src/services/event-capture/anonymous-data-capture/index.js',
      {
        deckeeId:
          '[initialize] anonymous-data-capture clear events if not session',
      },
    );
  }
};
